import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Plus, Pencil, Trash2, ExternalLink, CheckCircle2, XCircle } from 'lucide-react';
import { fetchPriceComparison, saveCompetitorPrice, deleteCompetitorPrice } from '../services/api';
import logoCp from '../logo-cp.svg';

// ─────────────────────────────────────────────────────────────────────────────
// Category registry. Each category defines its own row bands + matching +
// normalization metric, because "same product" means something different per
// category: inches point-to-point (crabs), oz per tail (lobster), grade +
// $/lb (crab meat), oz per cake (crab cakes), cluster oz + $/lb (snow crab).
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { key: 'steamed_blue_crabs', label: 'Steamed Blue Crabs' },
  { key: 'lobster_tails', label: 'Lobster Tails' },
  { key: 'crab_meat', label: 'Crab Meat' },
  { key: 'crab_cakes', label: 'Crab Cakes' },
  { key: 'snow_crab', label: 'Snow Crab' },
];

// Our steamed blue crab grades (point-to-point ranges from our PDP).
const OUR_GRADES = [
  { code: 'STMHC', label: 'Standards', gender: 'male', min: 5, max: 5.5, rangeText: '5" – 5½"', bushelCount: 78 },
  { code: 'SLMHC', label: 'Selects', gender: 'male', min: 5.5, max: 6, rangeText: '5½" – 6"', bushelCount: 66 },
  { code: 'PMHC', label: 'Premiums', gender: 'male', min: 6, max: 6.75, rangeText: '6" – 6¾"', bushelCount: 54 },
  { code: 'CMHC', label: 'Colossals', gender: 'male', min: 6.75, max: null, rangeText: '6¾" & up', bushelCount: 48 },
  { code: 'STFHC', label: 'Standard Females', gender: 'female', min: 5.5, max: 6.5, rangeText: '5½" – 6½"', bushelCount: 72 },
  { code: 'SLFHC', label: 'Select Females', gender: 'female', min: 6.5, max: null, rangeText: '6½" & up', bushelCount: 72 },
];

const UNIT_TABS = [
  { key: 'half_dozen', label: 'Half Dozen', units: ['half_dozen'], ourUnit: 'HD', ourCount: 6 },
  // Matrix shows single-dozen only for a clean dozen-to-dozen read. 2/3-dozen rows
  // stay in the DB + the "All listings" view for later analysis, just hidden here.
  { key: 'dozen', label: 'Dozen', units: ['dozen'], ourUnit: 'DZ', ourCount: 12 },
  { key: 'half_bushel', label: 'Half Bushel', units: ['half_bushel'], ourUnit: 'HB', ourCount: null },
  { key: 'bushel', label: 'Bushel', units: ['bushel'], ourUnit: 'BU', ourCount: null },
];

const UNIT_LABELS = {
  half_dozen: '½ Dozen', dozen: 'Dozen', two_dozen: '2 Dozen', three_dozen: '3 Dozen',
  half_bushel: '½ Bushel', bushel: 'Bushel', each: 'Each', pack: 'Pack', container: 'Container',
};

const num = (v) => (v == null || v === '' ? null : parseFloat(v));
const fmt$ = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const eff = (row) => num(row.sale_price) ?? num(row.price);

function specText(minV, maxV, unit, estimated) {
  if (minV == null && maxV == null) return 'spec n/a';
  const u = unit === 'oz' ? ' oz' : '"';
  const t = maxV == null ? `${minV}${u}+` : `${minV}–${maxV}${u}`;
  return estimated ? `~${t} (est)` : t;
}

function overlaps(aMin, aMax, bMin, bMax) {
  const a0 = aMin == null ? 0 : aMin, a1 = aMax == null ? 999 : aMax;
  const b0 = bMin == null ? 0 : bMin, b1 = bMax == null ? 999 : bMax;
  return a0 < b1 && b0 < a1;
}

// Per-crab unit cost — hidden for now (keeps the cells narrow). Flip to true to restore.
const SHOW_PER_CRAB = false;

// How closely a competitor's published size range lines up with our grade's range.
// 'exact' → same min & max (within tolerance) and not an estimate; else 'off'.
function sizeMatch(g, sizeMin, sizeMax, estimated) {
  if (estimated || sizeMin == null) return 'off';
  const tol = 0.1; // inches
  const minOk = Math.abs(sizeMin - g.min) <= tol;
  const maxOk = g.max == null ? sizeMax == null : (sizeMax != null && Math.abs(sizeMax - g.max) <= tol);
  return minOk && maxOk ? 'exact' : 'off';
}

function MatchIcon({ kind }) {
  return kind === 'exact'
    ? <CheckCircle2 size={14} strokeWidth={2.5} color="#16a34a" style={{ flex: '0 0 auto', verticalAlign: '-2px' }} aria-label="Exact size match" />
    : <XCircle size={14} strokeWidth={2.5} color="#d97706" style={{ flex: '0 0 auto', verticalAlign: '-2px' }} aria-label="Size slightly off" />;
}

// ── Per-category matrix builders ─────────────────────────────────────────────
// Each returns rows of: { key, title, sub, ourLines: [str], ourMetric, offers(competitorId) }
// where an offer = { id, head, priceMain, priceStrike, subLine, metric }.
// Delta chips = (offer.metric − ourMetric) / ourMetric.

const LOBSTER_ROWS = [
  { key: 'sub6', title: 'Small', sub: 'under 6 oz/tail', min: 0, max: 6, grade: null },
  { key: 'MD', title: 'Medium', sub: '6–7 oz/tail', min: 6, max: 7, grade: 'MD' },
  { key: 'SL', title: 'Select', sub: '7–8 oz/tail', min: 7, max: 8, grade: 'SL' },
  { key: 'LG', title: 'Large', sub: '8–10 oz/tail', min: 8, max: 10, grade: 'LG' },
  { key: 'mid', title: '10–12 oz', sub: '10–12 oz/tail', min: 10, max: 12, grade: null },
  { key: 'XL', title: 'X-Large', sub: '12–14 oz/tail', min: 12, max: 14, grade: 'XL' },
  { key: 'XJ', title: 'Jumbo', sub: '14–20 oz/tail', min: 14, max: 20, grade: 'XJ' },
  { key: 'mon', title: 'Monster', sub: '20 oz+/tail', min: 20, max: null, grade: null },
];
const LOB_PACKS = { '2PK': 2, '4PK': 4, '8PK': 8, '20PK': 20, '20': 20 };
const LOB_ALIAS = { ME: 'MD', SE: 'SL', LA: 'LG' };

function buildLobster(ours, prices, pickOur = eff) {
  // ours: k1 = grade (MD/SL/LG/XL/XJ + legacy aliases), k2 = pack
  const packsByGrade = {};
  for (const o of ours) {
    const grade = LOB_ALIAS[o.k1] || o.k1;
    const count = LOB_PACKS[o.k2];
    if (!count) continue;
    const p = pickOur(o);
    if (p == null) continue;
    (packsByGrade[grade] = packsByGrade[grade] || []).push({ count, eff: p, perTail: p / count });
  }
  for (const g of Object.keys(packsByGrade)) packsByGrade[g].sort((a, b) => a.count - b.count);
  return LOBSTER_ROWS.map(r => {
    const packs = r.grade ? (packsByGrade[r.grade] || []) : [];
    const two = packs.find(p => p.count === 2);
    const best = packs.reduce((m, p) => (m == null || p.perTail < m.perTail ? p : m), null);
    const ourLines = packs.length ? [
      two ? `2-pk ${fmt$(two.eff)} (${fmt$(two.perTail)}/tail)` : '',
      best && (!two || best.count !== 2) ? `best: ${best.count}-pk ${fmt$(best.eff)} (${fmt$(best.perTail)}/tail)` : '',
    ].filter(Boolean) : [];
    const closestOur = (count) => {
      if (!packs.length) return null;
      return packs.reduce((m, p) => (m == null || Math.abs(Math.log(p.count / count)) < Math.abs(Math.log(m.count / count)) ? p : m), null);
    };
    return {
      key: r.key, title: r.title, sub: r.sub, ourLines,
      offers: (row) => {
        if (!overlaps(num(row.size_min_in), num(row.size_max_in), r.min, r.max)) return null;
        const e = eff(row); if (e == null) return null;
        const perTail = row.count ? e / row.count : null;
        const base = row.count ? closestOur(row.count) : null;
        return {
          head: `${row.size_label} · ${specText(num(row.size_min_in), num(row.size_max_in), 'oz', row.size_estimated)}${row.count ? ` · ${row.count} tails` : ''}`,
          priceMain: e, priceStrike: num(row.sale_price) != null && num(row.sale_price) < num(row.price) ? num(row.price) : null,
          subLine: perTail != null ? `${fmt$(perTail)}/tail` : 'count not stated',
          metric: perTail, ourMetric: base ? base.perTail : null,
        };
      },
    };
  });
}

const MEAT_GRADES = [
  { key: 'colossal', title: 'Colossal Lump', ourCode: null },
  { key: 'jumbo_lump', title: 'Jumbo Lump', ourCode: 'MJL' },
  { key: 'lump', title: 'Lump', ourCode: 'MGL' },
  { key: 'backfin', title: 'Backfin', ourCode: 'MB' },
  { key: 'special', title: 'Special', ourCode: null },
  { key: 'claw', title: 'Claw', ourCode: 'MCW' },
];
function normGrade(row) {
  const s = `${row.size_label} ${row.product_name}`.toLowerCase();
  if (/colossal/.test(s)) return 'colossal';
  if (/jumbo/.test(s)) return 'jumbo_lump';
  if (/backfin|back fin/.test(s)) return 'backfin';
  if (/special/.test(s)) return 'special';
  if (/claw|cocktail/.test(s)) return 'claw';
  if (/lump/.test(s)) return 'lump';
  return 'mixed';
}

function buildCrabMeat(ours, prices, pickOur = eff) {
  // ours: k1 = FR/PA, k2 = grade code, k3 = 1LB/3LBS/5LBS. Compare on the 1 lb container.
  const oneLb = {};
  for (const o of ours) {
    if (!/^1LB/.test(o.k3)) continue;
    const p = pickOur(o); if (p == null) continue;
    oneLb[`${o.k2}|${o.k1}`] = p;
  }
  return MEAT_GRADES.map(g => {
    const fr = g.ourCode ? oneLb[`${g.ourCode}|FR`] : null;
    const pa = g.ourCode ? oneLb[`${g.ourCode}|PA`] : null;
    const ourLines = [
      fr != null ? `Fresh 1 lb ${fmt$(fr)}` : '',
      pa != null ? `Pasteurized 1 lb ${fmt$(pa)}` : '',
    ].filter(Boolean);
    return {
      key: g.key, title: g.title, sub: 'per 1 lb container', ourLines,
      offers: (row) => {
        if (normGrade(row) !== g.key) return null;
        const e = eff(row); if (e == null) return null;
        const netOz = num(row.net_oz);
        const perLb = netOz ? e / (netOz / 16) : null;
        const state = /pasteurized/i.test(row.notes || '') || /pasteurized/i.test(row.product_name) ? 'PA'
          : /fresh/i.test(row.notes || '') || /fresh/i.test(row.product_name) ? 'FR' : null;
        const base = state === 'FR' ? (fr ?? pa) : state === 'PA' ? (pa ?? fr) : (pa ?? fr);
        return {
          head: `${netOz ? `${netOz % 16 === 0 ? `${netOz / 16} lb` : `${netOz} oz`}` : 'size n/a'}${state ? ` · ${state === 'FR' ? 'fresh' : 'pasteurized'}` : ''}`,
          priceMain: e, priceStrike: num(row.sale_price) != null && num(row.sale_price) < num(row.price) ? num(row.price) : null,
          subLine: perLb != null ? `${fmt$(perLb)}/lb` : '',
          metric: perLb, ourMetric: base ?? null,
        };
      },
    };
  });
}

const CAKE_ROWS = [
  { key: '2OZ', title: 'Cocktail', sub: 'under 3 oz cakes', min: 0, max: 3, ourCode: '2OZ' },
  { key: '4OZ', title: '~4 oz cakes', sub: '3–5 oz', min: 3, max: 5, ourCode: '4OZ' },
  { key: '6OZ', title: '~6 oz cakes', sub: '5–7 oz', min: 5, max: 7, ourCode: '6OZ' },
  { key: 'big', title: '8 oz+ cakes', sub: 'jumbo/colossal cakes', min: 7, max: null, ourCode: null },
];
function buildCrabCakes(ours, prices, pickOur = eff) {
  // ours: k1 = 2OZ/4OZ/6OZ, k2 = AJ/OR, k3 = HD/DZ/2DZ. Base = per-cake off the dozen.
  const dz = {};
  for (const o of ours) {
    if (o.k3 !== 'DZ') continue;
    const p = pickOur(o); if (p == null) continue;
    dz[`${o.k1}|${o.k2}`] = p / 12;
  }
  return CAKE_ROWS.map(r => {
    const or = r.ourCode ? dz[`${r.ourCode}|OR`] : null;
    const aj = r.ourCode ? dz[`${r.ourCode}|AJ`] : null;
    const cakeOzMid = r.ourCode ? parseInt(r.ourCode, 10) : null;
    const ourLines = [
      or != null ? `Original ${fmt$(or)}/cake (dz)` : '',
      aj != null ? `All-Jumbo ${fmt$(aj)}/cake (dz)` : '',
    ].filter(Boolean);
    const ourPerOz = or != null && cakeOzMid ? or / cakeOzMid : null;
    return {
      key: r.key, title: r.title, sub: r.sub, ourLines,
      offers: (row) => {
        const oz = num(row.size_min_in); // cake weight in oz (min=max)
        if (oz == null || !overlaps(oz, num(row.size_max_in) ?? oz, r.min, r.max)) return null;
        const e = eff(row); if (e == null) return null;
        const perCake = row.count ? e / row.count : null;
        const perOz = perCake != null && oz ? perCake / oz : null;
        return {
          head: `${row.count ? `${row.count} × ` : ''}${oz} oz${row.size_label ? ` · ${row.size_label}` : ''}`,
          priceMain: e, priceStrike: num(row.sale_price) != null && num(row.sale_price) < num(row.price) ? num(row.price) : null,
          subLine: perCake != null ? `${fmt$(perCake)}/cake${perOz != null ? ` (${fmt$(perOz)}/oz)` : ''}` : '',
          metric: perOz, ourMetric: ourPerOz,
        };
      },
    };
  });
}

const SNOW_ROWS = [
  { key: 'LG', title: 'Large clusters', sub: '~5–8 oz clusters', min: 5, max: 8, ourCode: 'LG' },
  { key: 'JMB', title: 'Jumbo clusters', sub: '~8–10 oz clusters', min: 8, max: 10, ourCode: 'JMB' },
  { key: 'COL', title: 'Colossal clusters', sub: '~10 oz+ clusters', min: 10, max: null, ourCode: 'COL' },
  { key: 'na', title: 'Ungraded / legs', sub: 'no cluster size stated', min: null, max: null, ourCode: 'JMB' },
];
function buildSnowCrab(ours, prices, pickOur = eff) {
  // ours: k1 = AL (Bairdi) / OP (Opilio), k2 = LG/JMB/COL, k3 = 3LBS/6LBS/9LBS.
  // Compare per lb off the 3 lb box (our smallest).
  const perLb = {};
  for (const o of ours) {
    if (o.k3 !== '3LBS') continue;
    const p = pickOur(o); if (p == null) continue;
    perLb[`${o.k2}|${o.k1}`] = p / 3;
  }
  return SNOW_ROWS.map(r => {
    const op = perLb[`${r.ourCode}|OP`];
    const al = perLb[`${r.ourCode}|AL`];
    const ourLines = [
      op != null ? `Opilio ${fmt$(op)}/lb (3 lb box)` : '',
      al != null ? `Bairdi ${fmt$(al)}/lb (3 lb box)` : '',
    ].filter(r.key === 'na' ? (s, i) => i === 0 && s : Boolean);
    return {
      key: r.key, title: r.title, sub: r.key === 'na' ? 'compared vs our Jumbo Opilio' : r.sub, ourLines,
      offers: (row) => {
        const cMin = num(row.size_min_in), cMax = num(row.size_max_in);
        const graded = cMin != null || cMax != null;
        if (r.key === 'na' ? graded : !(graded && overlaps(cMin, cMax, r.min, r.max))) return null;
        const e = eff(row); if (e == null) return null;
        const netOz = num(row.net_oz);
        const lbs = netOz ? netOz / 16 : null;
        const pl = lbs ? e / lbs : null;
        return {
          head: `${lbs ? `${lbs % 1 === 0 ? lbs : lbs.toFixed(1)} lb` : 'weight n/a'}${row.size_label ? ` · ${row.size_label}` : ''}${graded ? ` · ${specText(cMin, cMax, 'oz', row.size_estimated)}` : ''}`,
          priceMain: e, priceStrike: num(row.sale_price) != null && num(row.sale_price) < num(row.price) ? num(row.price) : null,
          subLine: pl != null ? `${fmt$(pl)}/lb` : '',
          metric: pl, ourMetric: op ?? al ?? null,
        };
      },
    };
  });
}

const BUILDERS = {
  lobster_tails: buildLobster,
  crab_meat: buildCrabMeat,
  crab_cakes: buildCrabCakes,
  snow_crab: buildSnowCrab,
};

const CATEGORY_FOOTNOTES = {
  lobster_tails: 'Deltas compare $/tail against our closest pack size. Cold-water vs warm-water origin is noted per listing where the site states it.',
  crab_meat: 'Compared per pound on the 1 lb container where possible. Fresh is matched against our fresh price, pasteurized against pasteurized. Watch the notes for imported (non-blue-crab) meat — it is not apples-to-apples.',
  crab_cakes: 'Deltas compare $/oz of cake against our Original recipe (All-Jumbo shown for reference). Cake weights come from each site; recipes and fillers differ, so treat close calls as parity.',
  snow_crab: 'Compared $/lb off our 3 lb box (Opilio). Cluster-size grades are approximate — most competitors sell ungraded; those land in the "Ungraded / legs" row vs our Jumbo Opilio.',
};

// ─────────────────────────────────────────────────────────────────────────────

export default function PriceComparison() {
  const [category, setCategory] = useState('steamed_blue_crabs');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unitTab, setUnitTab] = useState('dozen');
  const [priceBasis, setPriceBasis] = useState('vip'); // 'vip' (member price) | 'market' (regular price)
  const [view, setView] = useState('matrix');
  const [listCompetitor, setListCompetitor] = useState('all');
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async (cat) => {
    setLoading(true); setError(null);
    try { setData(await fetchPriceComparison(cat)); }
    catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(category); }, [category]);

  const competitors = useMemo(
    () => (data?.competitors || []).filter(c => (data?.prices || []).some(p => p.competitor_id === c.id)),
    [data]
  );
  const allCompetitors = data?.competitors || [];
  const prices = data?.prices || [];
  const compById = useMemo(() => Object.fromEntries(allCompetitors.map(c => [c.id, c])), [allCompetitors]);

  const oursByKey = useMemo(() => {
    const m = {};
    if (category === 'steamed_blue_crabs') {
      for (const r of (data?.ours || [])) m[`${r.grade_code}|${r.unit_code}`] = { price: num(r.price), sale: num(r.sale_price) };
    }
    return m;
  }, [data, category]);

  const lastCaptured = useMemo(() => {
    let d = null;
    for (const p of prices) { const c = (p.captured_at || '').slice(0, 10); if (c && (!d || c > d)) d = c; }
    return d;
  }, [prices]);

  const tab = UNIT_TABS.find(t => t.key === unitTab);

  // Our comparison basis. VIP = member price (sale_price, falling back to regular);
  // Market = regular shelf price. Competitor prices always use their own effective price.
  const pickOur = useMemo(
    () => (priceBasis === 'market'
      ? (o) => num(o.price)
      : (o) => num(o.sale_price) ?? num(o.price)),
    [priceBasis]
  );

  // Steamed blue crabs matrix (unit-tabbed, gender-aware).
  const steamedMatrix = useMemo(() => {
    if (category !== 'steamed_blue_crabs' || !tab) return [];
    return OUR_GRADES.map(g => {
      const our = oursByKey[`${g.code}|${tab.ourUnit}`] || null;
      const ourVip = our ? (our.sale ?? our.price) : null;   // member price
      const ourMarket = our ? our.price : null;              // regular shelf price
      const ourEff = priceBasis === 'market' ? ourMarket : ourVip;
      const ourAlt = priceBasis === 'market' ? ourVip : ourMarket; // the "other" price, for context
      const ourCount = tab.ourCount || Math.round(g.bushelCount / (tab.key === 'half_bushel' ? 2 : 1));
      const ourPerCrab = ourEff != null && ourCount ? ourEff / ourCount : null;
      const cells = competitors.map(c => {
        const offers = prices
          .filter(p => p.competitor_id === c.id
            && tab.units.includes(p.unit)
            && p.gender === g.gender
            && overlaps(num(p.size_min_in), num(p.size_max_in), g.min, g.max))
          .map(p => {
            const e = eff(p);
            const perCrab = p.count ? e / p.count : null;
            let deltaPct = null;
            if (perCrab != null && ourPerCrab != null) deltaPct = (perCrab - ourPerCrab) / ourPerCrab * 100;
            else if (e != null && ourEff != null && !p.count && (tab.key === 'half_bushel' || tab.key === 'bushel')) deltaPct = (e - ourEff) / ourEff * 100;
            return { ...p, eff: e, perCrab, deltaPct };
          })
          .sort((a, b) => (a.eff ?? 9e9) - (b.eff ?? 9e9));
        return { competitor: c, offers };
      });
      return { grade: g, our, ourEff, ourAlt, ourCount, ourPerCrab, cells };
    });
  }, [category, tab, oursByKey, competitors, prices, priceBasis]);

  // Generic matrix for the other categories.
  const genericMatrix = useMemo(() => {
    const builder = BUILDERS[category];
    if (!builder || !data) return [];
    const rows = builder(data.ours || [], prices, pickOur);
    return rows.map(r => ({
      ...r,
      cells: competitors.map(c => {
        const offers = prices
          .filter(p => p.competitor_id === c.id)
          .map(p => { const o = r.offers(p); return o ? { ...o, id: p.id, url: p.url, notes: p.notes } : null; })
          .filter(Boolean)
          .map(o => ({ ...o, deltaPct: o.metric != null && o.ourMetric != null ? (o.metric - o.ourMetric) / o.ourMetric * 100 : null }))
          .sort((a, b) => (a.priceMain ?? 9e9) - (b.priceMain ?? 9e9));
        return { competitor: c, offers };
      }),
    }));
  }, [category, data, competitors, prices, pickOur]);

  const summary = useMemo(() => {
    let matchups = 0, wins = 0;
    const rows = category === 'steamed_blue_crabs'
      ? steamedMatrix.map(r => ({ has: r.ourEff != null, deltas: r.cells.flatMap(c => c.offers).map(o => o.deltaPct).filter(d => d != null) }))
      : genericMatrix.map(r => ({ has: r.ourLines.length > 0, deltas: r.cells.flatMap(c => c.offers).map(o => o.deltaPct).filter(d => d != null) }));
    for (const r of rows) {
      if (!r.has || !r.deltas.length) continue;
      matchups++;
      if (r.deltas.every(d => d >= 0)) wins++;
    }
    return { matchups, wins };
  }, [category, steamedMatrix, genericMatrix]);

  const listRows = useMemo(() => prices
    .filter(p => listCompetitor === 'all' || String(p.competitor_id) === String(listCompetitor))
    .sort((a, b) => (compById[a.competitor_id]?.name || '').localeCompare(compById[b.competitor_id]?.name || '')
      || (a.gender || '').localeCompare(b.gender || '') || (num(a.size_min_in) ?? 0) - (num(b.size_min_in) ?? 0)),
  [prices, listCompetitor, compById]);

  const onSaveRow = async (form) => {
    setSaving(true);
    try {
      await saveCompetitorPrice({ ...form, category });
      setEditRow(null);
      await load(category);
    } catch (e) { window.alert(`Save failed: ${e.message}`); }
    finally { setSaving(false); }
  };

  const onDeleteRow = async (row) => {
    if (!window.confirm(`Delete ${compById[row.competitor_id]?.name || ''} — ${row.size_label || row.product_name}?`)) return;
    try { await deleteCompetitorPrice(row.id); await load(category); }
    catch (e) { window.alert(`Delete failed: ${e.message}`); }
  };

  const catLabel = CATEGORIES.find(c => c.key === category)?.label || '';

  // Brand treatment so our column stands out while scanning across competitors.
  const ourHeadStyle = { minWidth: 230, background: 'var(--navy, #00395C)', color: '#fff', borderBottom: '3px solid var(--orange, #DC582A)', verticalAlign: 'middle' };
  const ourColStyle = { background: 'rgba(220, 88, 42, 0.055)', borderLeft: '3px solid var(--orange, #DC582A)', borderRight: '1px solid rgba(0, 57, 92, 0.12)', verticalAlign: 'top' };
  const basisLabel = priceBasis === 'market' ? 'Market' : 'VIP';
  const brandHead = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ background: '#fff', borderRadius: 6, padding: '4px 7px', display: 'inline-flex', boxShadow: '0 1px 2px rgba(0,0,0,0.18)' }}>
        <img src={logoCp} alt="Crab Place" style={{ height: 30, width: 'auto', display: 'block' }} />
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', opacity: 0.85 }}>{basisLabel} price</span>
    </div>
  );
  const basisTag = (
    <span style={{
      fontSize: 10, fontWeight: 800, letterSpacing: 0.4, padding: '1px 5px', borderRadius: 4,
      background: priceBasis === 'market' ? 'var(--navy, #00395C)' : 'var(--orange, #DC582A)', color: '#fff',
    }}>{basisLabel.toUpperCase()}</span>
  );

  const deltaChip = (deltaPct) => deltaPct == null ? null : (
    <span style={{
      marginLeft: 6, fontSize: 12, fontWeight: 700, padding: '1px 6px', borderRadius: 10,
      background: deltaPct >= 0 ? '#dcfce7' : '#fee2e2',
      color: deltaPct >= 0 ? '#166534' : '#b91c1c',
    }}>
      {deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(0)}%
    </span>
  );

  return (
    <div className="page-content">
      <div className="breadcrumb">
        <a href="#inventory" onClick={(e) => e.preventDefault()}>Product Inventory</a>
        <span> / </span>Price Comparison
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 4 }}>
        <div>
          <h1 style={{ margin: 0 }}>Price Comparison — {catLabel}</h1>
          <div style={{ color: 'var(--gray-500, #6b7280)', fontSize: 13, marginTop: 4 }}>
            SKU-to-SKU shelf prices. Shipping, packaging, and fees are <strong>not</strong> included yet — total-delivered-price is the planned next step.
            {lastCaptured && <> Competitor prices captured <strong>{lastCaptured}</strong>. Ask Claude to re-scrape to refresh.</>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={() => load(category)} disabled={loading}>
            <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setEditRow({})}>
            <Plus size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />Add competitor price
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, margin: '12px 0 4px', flexWrap: 'wrap' }}>
        {CATEGORIES.map(c => (
          <button key={c.key} className={`btn ${category === c.key ? 'btn-primary' : 'btn-outline'}`} onClick={() => { setCategory(c.key); setView('matrix'); }}>
            {c.label}
          </button>
        ))}
      </div>

      {error && <div className="card" style={{ padding: 16, color: 'var(--danger, #b91c1c)' }}>{error}</div>}
      {loading && !data && <div className="card" style={{ padding: 24 }}>Loading…</div>}

      {data && (
        <>
          <div style={{ display: 'flex', gap: 8, margin: '10px 0', flexWrap: 'wrap', alignItems: 'center' }}>
            {category === 'steamed_blue_crabs' && UNIT_TABS.map(t => (
              <button key={t.key} className={`btn ${unitTab === t.key ? 'btn-navy' : 'btn-outline'}`} onClick={() => setUnitTab(t.key)}>
                {t.label}
              </button>
            ))}
            <span style={{ flex: 1 }} />
            {view === 'matrix' && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginRight: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', fontWeight: 600 }}>Compare vs</span>
                <div style={{ display: 'inline-flex', border: '1px solid var(--gray-300, #d1d5db)', borderRadius: 8, overflow: 'hidden' }}>
                  <button
                    onClick={() => setPriceBasis('vip')}
                    style={{ border: 'none', cursor: 'pointer', padding: '7px 13px', fontWeight: 700, fontSize: 13,
                      background: priceBasis === 'vip' ? 'var(--orange, #DC582A)' : '#fff',
                      color: priceBasis === 'vip' ? '#fff' : 'var(--navy, #00395C)' }}>
                    VIP price
                  </button>
                  <button
                    onClick={() => setPriceBasis('market')}
                    style={{ border: 'none', borderLeft: '1px solid var(--gray-300, #d1d5db)', cursor: 'pointer', padding: '7px 13px', fontWeight: 700, fontSize: 13,
                      background: priceBasis === 'market' ? 'var(--navy, #00395C)' : '#fff',
                      color: priceBasis === 'market' ? '#fff' : 'var(--navy, #00395C)' }}>
                    Market price
                  </button>
                </div>
              </div>
            )}
            <button className={`btn ${view === 'matrix' ? 'btn-navy' : 'btn-ghost'}`} onClick={() => setView('matrix')}>Matrix</button>
            <button className={`btn ${view === 'list' ? 'btn-navy' : 'btn-ghost'}`} onClick={() => setView('list')}>All listings ({prices.length})</button>
          </div>

          {view === 'matrix' && summary.matchups > 0 && (
            <div style={{ marginBottom: 10, fontSize: 14 }}>
              We are the cheapest (normalized) in <strong>{summary.wins} of {summary.matchups}</strong> matchups here.
              <span style={{ color: 'var(--gray-500, #6b7280)' }}> Measured against our <strong>{basisLabel.toLowerCase()} price</strong>. Green = competitor is more expensive than us; red = they undercut us.</span>
              {category === 'steamed_blue_crabs' && (
                <span style={{ color: 'var(--gray-500, #6b7280)', display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6 }}>
                  <CheckCircle2 size={14} strokeWidth={2.5} color="#16a34a" style={{ verticalAlign: '-2px' }} /> exact size match ·
                  <XCircle size={14} strokeWidth={2.5} color="#d97706" style={{ verticalAlign: '-2px' }} /> size slightly off
                </span>
              )}
            </div>
          )}

          {view === 'matrix' && competitors.length === 0 && (
            <div className="card" style={{ padding: 20 }}>No competitor prices loaded for this category yet.</div>
          )}

          {view === 'matrix' && competitors.length > 0 && category === 'steamed_blue_crabs' && (
            <div className="card" style={{ overflowX: 'auto' }}>
              <table className="product-table" style={{ minWidth: 1100 }}>
                <thead>
                  <tr>
                    <th style={ourHeadStyle}>{brandHead}</th>
                    {competitors.map(c => <th key={c.id} style={{ minWidth: 170 }}>{c.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {steamedMatrix.map(row => (
                    <tr key={row.grade.code}>
                      <td style={ourColStyle}>
                        <div style={{ fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 4 }}>
                          {row.grade.label} <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>({row.grade.rangeText})</span>
                        </div>
                        {row.ourEff == null ? <span style={{ color: '#9ca3af' }}>not sold</span> : (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--navy, #00395C)' }}>{fmt$(row.ourEff)}</span>
                              {basisTag}
                              {row.ourAlt != null && row.ourAlt !== row.ourEff && (
                                priceBasis === 'vip'
                                  ? <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', textDecoration: 'line-through' }}>{fmt$(row.ourAlt)}</span>
                                  : <span style={{ fontSize: 12, color: 'var(--orange, #DC582A)', fontWeight: 600 }}>VIP {fmt$(row.ourAlt)}</span>
                              )}
                            </div>
                            {SHOW_PER_CRAB && row.ourPerCrab != null && (
                              <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', whiteSpace: 'nowrap' }}>
                                {(tab.key === 'half_bushel' || tab.key === 'bushel') ? `~${row.ourCount} crabs · ~` : ''}
                                ${row.ourPerCrab.toFixed(2)}/crab
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      {row.cells.map(cell => (
                        <td key={cell.competitor.id} style={{ verticalAlign: 'top' }}>
                          {cell.offers.length === 0 ? <span style={{ color: '#9ca3af' }}>—</span> : cell.offers.map(o => (
                            <div key={o.id} style={{ marginBottom: 8 }}>
                              <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                                <MatchIcon kind={sizeMatch(row.grade, num(o.size_min_in), num(o.size_max_in), o.size_estimated)} />
                                <span>
                                  {o.size_label} · {specText(num(o.size_min_in), num(o.size_max_in), 'in', o.size_estimated)}
                                  {tab.units.length > 1 && o.unit !== 'dozen' ? ` · ${UNIT_LABELS[o.unit]}` : ''}
                                </span>
                              </div>
                              <div>
                                <span style={{ fontWeight: 700 }}>{fmt$(o.eff)}</span>
                                {num(o.sale_price) != null && num(o.sale_price) < num(o.price) && (
                                  <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', textDecoration: 'line-through', marginLeft: 6 }}>{fmt$(num(o.price))}</span>
                                )}
                                {deltaChip(o.deltaPct)}
                              </div>
                              {SHOW_PER_CRAB && (
                                <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>
                                  {o.perCrab != null ? `$${o.perCrab.toFixed(2)}/crab` : (tab.key === 'half_bushel' || tab.key === 'bushel') ? 'count not stated' : ''}
                                </div>
                              )}
                            </div>
                          ))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {view === 'matrix' && competitors.length > 0 && category !== 'steamed_blue_crabs' && (
            <div className="card" style={{ overflowX: 'auto' }}>
              <table className="product-table" style={{ minWidth: 1000 }}>
                <thead>
                  <tr>
                    <th style={ourHeadStyle}>{brandHead}</th>
                    {competitors.map(c => <th key={c.id} style={{ minWidth: 180 }}>{c.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {genericMatrix.map(row => (
                    <tr key={row.key}>
                      <td style={ourColStyle}>
                        <div style={{ fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 4 }}>
                          {row.title} <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>({row.sub})</span>
                        </div>
                        {row.ourLines.length === 0 ? <span style={{ color: '#9ca3af' }}>not sold</span> :
                          row.ourLines.map((l, i) => (
                            <div key={i} style={{ fontWeight: i === 0 ? 700 : 400, fontSize: i === 0 ? 14 : 12, color: i === 0 ? 'var(--navy, #00395C)' : 'inherit' }}>
                              {i === 0 && <span style={{ marginRight: 6 }}>{basisTag}</span>}{l}
                            </div>
                          ))}
                      </td>
                      {row.cells.map(cell => (
                        <td key={cell.competitor.id} style={{ verticalAlign: 'top' }}>
                          {cell.offers.length === 0 ? <span style={{ color: '#9ca3af' }}>—</span> : cell.offers.map(o => (
                            <div key={o.id} style={{ marginBottom: 8 }}>
                              <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>{o.head}</div>
                              <div>
                                <span style={{ fontWeight: 700 }}>{fmt$(o.priceMain)}</span>
                                {o.priceStrike != null && (
                                  <span style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', textDecoration: 'line-through', marginLeft: 6 }}>{fmt$(o.priceStrike)}</span>
                                )}
                                {deltaChip(o.deltaPct)}
                              </div>
                              {o.subLine && <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)' }}>{o.subLine}</div>}
                            </div>
                          ))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {view === 'matrix' && (
            <div style={{ fontSize: 12, color: 'var(--gray-500, #6b7280)', marginTop: 8 }}>
              {category === 'steamed_blue_crabs'
                ? 'A competitor grade that spans one of our size boundaries appears in every row it overlaps. "(est)" = the site does not publish sizes; the range is our estimate. Bushel counts vary by seller — the per-crab price is the honest comparison there.'
                : CATEGORY_FOOTNOTES[category]}
            </div>
          )}

          {view === 'list' && (
            <div className="card" style={{ overflowX: 'auto' }}>
              <div style={{ padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'center' }}>
                <label className="form-label" style={{ margin: 0 }}>Competitor</label>
                <select className="form-select" style={{ maxWidth: 260 }} value={listCompetitor} onChange={e => setListCompetitor(e.target.value)}>
                  <option value="all">All competitors</option>
                  {allCompetitors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <table className="product-table" style={{ minWidth: 1000 }}>
                <thead>
                  <tr>
                    <th>Competitor</th><th>Product / grade</th><th>Spec</th>
                    <th>Unit</th><th>Count</th><th>Net wt</th><th>Price</th><th>Sale</th><th>Captured</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {listRows.map(r => (
                    <tr key={r.id}>
                      <td>{compById[r.competitor_id]?.name || r.competitor_id}</td>
                      <td>
                        {r.product_name}{r.size_label ? ` — ${r.size_label}` : ''}
                        {r.url && <a href={r.url} target="_blank" rel="noreferrer" style={{ marginLeft: 6 }}><ExternalLink size={12} /></a>}
                      </td>
                      <td>{specText(num(r.size_min_in), num(r.size_max_in), r.size_unit, r.size_estimated)}</td>
                      <td>{UNIT_LABELS[r.unit] || r.unit}</td>
                      <td>{r.count ?? '—'}</td>
                      <td>{r.net_oz != null ? (num(r.net_oz) % 16 === 0 ? `${num(r.net_oz) / 16} lb` : `${num(r.net_oz)} oz`) : '—'}</td>
                      <td>{fmt$(num(r.price))}</td>
                      <td>{r.sale_price != null ? fmt$(num(r.sale_price)) : '—'}</td>
                      <td>{(r.captured_at || '').slice(0, 10)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-ghost" title="Edit" onClick={() => setEditRow(r)}><Pencil size={14} /></button>
                        <button className="btn btn-ghost" title="Delete" onClick={() => onDeleteRow(r)}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {editRow !== null && (
        <EditModal
          row={editRow}
          category={category}
          competitors={allCompetitors}
          saving={saving}
          onCancel={() => setEditRow(null)}
          onSave={onSaveRow}
        />
      )}
    </div>
  );
}

function EditModal({ row, category, competitors, saving, onCancel, onSave }) {
  const isSteamed = category === 'steamed_blue_crabs';
  const [f, setF] = useState(() => ({
    id: row.id || null,
    competitor_id: row.competitor_id || (competitors[0]?.id ?? ''),
    product_name: row.product_name || '',
    size_label: row.size_label || '',
    size_min_in: row.size_min_in ?? '',
    size_max_in: row.size_max_in ?? '',
    size_unit: row.size_unit || (isSteamed ? 'in' : 'oz'),
    size_estimated: !!row.size_estimated,
    gender: row.gender || (isSteamed ? 'male' : 'na'),
    unit: row.unit || (isSteamed ? 'dozen' : 'pack'),
    count: row.count ?? '',
    net_oz: row.net_oz ?? '',
    price: row.price ?? '',
    sale_price: row.sale_price ?? '',
    url: row.url || '',
    notes: row.notes || '',
  }));
  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const submit = () => {
    if (!f.competitor_id || f.price === '' || !f.unit) { window.alert('Competitor, unit, and price are required.'); return; }
    onSave({
      ...f,
      competitor_id: Number(f.competitor_id),
      size_min_in: f.size_min_in === '' ? null : Number(f.size_min_in),
      size_max_in: f.size_max_in === '' ? null : Number(f.size_max_in),
      count: f.count === '' ? null : Number(f.count),
      net_oz: f.net_oz === '' ? null : Number(f.net_oz),
      price: Number(f.price),
      sale_price: f.sale_price === '' ? null : Number(f.sale_price),
    });
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="card" style={{ width: 560, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 20 }}>
        <h2 style={{ marginTop: 0 }}>{f.id ? 'Edit' : 'Add'} competitor price</h2>
        <div className="form-group">
          <label className="form-label">Competitor</label>
          <select className="form-select" value={f.competitor_id} onChange={set('competitor_id')} disabled={!!f.id}>
            {competitors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="form-group"><label className="form-label">Product name</label>
          <input className="form-input" value={f.product_name} onChange={set('product_name')} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group"><label className="form-label">Their size / grade label</label>
            <input className="form-input" value={f.size_label} onChange={set('size_label')} placeholder="e.g. Jumbo" /></div>
          {isSteamed ? (
            <div className="form-group"><label className="form-label">Gender</label>
              <select className="form-select" value={f.gender} onChange={set('gender')}>
                <option value="male">male</option><option value="female">female</option><option value="mixed">mixed</option>
              </select></div>
          ) : (
            <div className="form-group"><label className="form-label">Spec unit</label>
              <select className="form-select" value={f.size_unit} onChange={set('size_unit')}>
                <option value="oz">oz (per piece)</option><option value="in">inches</option>
              </select></div>
          )}
          <div className="form-group"><label className="form-label">Spec min ({f.size_unit})</label>
            <input className="form-input" type="number" step="0.25" value={f.size_min_in} onChange={set('size_min_in')} /></div>
          <div className="form-group"><label className="form-label">Spec max ({f.size_unit}, blank = open)</label>
            <input className="form-input" type="number" step="0.25" value={f.size_max_in} onChange={set('size_max_in')} /></div>
          <div className="form-group"><label className="form-label">Unit</label>
            <select className="form-select" value={f.unit} onChange={set('unit')}>
              {Object.entries(UNIT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div className="form-group"><label className="form-label">Piece count (if stated)</label>
            <input className="form-input" type="number" value={f.count} onChange={set('count')} /></div>
          <div className="form-group"><label className="form-label">Net weight (oz total)</label>
            <input className="form-input" type="number" step="0.5" value={f.net_oz} onChange={set('net_oz')} placeholder="e.g. 16 for 1 lb" /></div>
          <div className="form-group"><label className="form-label">Regular price ($)</label>
            <input className="form-input" type="number" step="0.01" value={f.price} onChange={set('price')} /></div>
          <div className="form-group"><label className="form-label">Sale price ($, blank = none)</label>
            <input className="form-input" type="number" step="0.01" value={f.sale_price} onChange={set('sale_price')} /></div>
        </div>
        <div className="form-group">
          <label style={{ fontSize: 13 }}>
            <input type="checkbox" checked={f.size_estimated} onChange={set('size_estimated')} style={{ marginRight: 6 }} />
            Spec is estimated (site doesn't publish it)
          </label>
        </div>
        <div className="form-group"><label className="form-label">Product URL</label>
          <input className="form-input" value={f.url} onChange={set('url')} /></div>
        <div className="form-group"><label className="form-label">Notes</label>
          <textarea className="form-textarea" rows={2} value={f.notes} onChange={set('notes')} /></div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
